import { workflowType as WorkflowTypeEnum } from 'services/dropdown-enums';

/**
 * Workflow Type Adapter
 * Centralizes all workflow type-specific logic including:
 * - Node availability and visibility
 * - Feature access control
 * - JSON payload formatting
 * - Validation rules
 */

export const WORKFLOW_TYPES = {
  AI: WorkflowTypeEnum.ai,
  STATIC: WorkflowTypeEnum.static
};

/**
 * Node type definitions with their workflow type support
 */
export const NODE_TYPE_CONFIG = {
  begin: {
    id: 'begin',
    name: 'Start Node',
    supportedTypes: [WORKFLOW_TYPES.AI, WORKFLOW_TYPES.STATIC],
    editable: false,
    deletable: false
  },
  end: {
    id: 'end',
    name: 'End Node',
    supportedTypes: [WORKFLOW_TYPES.AI, WORKFLOW_TYPES.STATIC],
    editable: false,
    deletable: false,
    features: {
      ai: ['text', 'endingStatement'],
      static: ['text']
    }
  },
  conversation: {
    id: 'conversation',
    name: 'Conversation Node',
    supportedTypes: [WORKFLOW_TYPES.AI, WORKFLOW_TYPES.STATIC],
    features: {
      ai: ['promptType', 'text', 'transitions', 'userOptions'],
      static: ['text', 'userOptions', 'userOptionHandles', 'isSkippable']
    }
  },
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge Node',
    supportedTypes: [WORKFLOW_TYPES.AI],
    features: {
      ai: ['transitions'],
      static: ['transitions']
    }
  },
  call_connect: {
    id: 'call_connect',
    name: 'Call Connect Node',
    supportedTypes: [WORKFLOW_TYPES.AI, WORKFLOW_TYPES.STATIC],
    features: {
      ai: ['success', 'failure'],
      static: ['success', 'failure']
    }
  },
  appointment: {
    id: 'appointment',
    name: 'Appointment Node',
    supportedTypes: [WORKFLOW_TYPES.AI, WORKFLOW_TYPES.STATIC],
    features: {
      ai: ['text', 'calendarId', 'requireConsent', 'transitions'],
      static: ['text', 'calendarId', 'requireConsent', 'transitions']
    }
  },
  chat_transfer: {
    id: 'chat_transfer',
    name: 'Chat Transfer Node',
    supportedTypes: [WORKFLOW_TYPES.AI],
    features: {
      ai: ['success', 'failure'],
      static: ['success', 'failure']
    }
  },
  variable: {
    id: 'variable',
    name: 'Extract Variable Node',
    supportedTypes: [WORKFLOW_TYPES.AI],
    features: {
      ai: ['variables', 'transitions'],
      static: ['variables', 'transitions']
    }
  },
  api: {
    id: 'api',
    name: 'API Node',
    supportedTypes: [WORKFLOW_TYPES.AI],
    features: {
      ai: ['config', 'success', 'failure'],
      static: ['config', 'success', 'failure']
    }
  },
  condition: {
    id: 'condition',
    name: 'Condition Node',
    supportedTypes: [WORKFLOW_TYPES.AI],
    features: {
      ai: ['transitions'],
      static: ['transitions']
    }
  }
};

/**
 * Workflow Type Adapter Class
 * Provides a unified interface for workflow type-specific operations
 */
export class WorkflowTypeAdapter {
  constructor(workflowType) {
    this.workflowType = workflowType;
    this.isStatic = workflowType === WORKFLOW_TYPES.STATIC;
    this.isAI = workflowType === WORKFLOW_TYPES.AI;
  }

  // ============================================================================
  // ACCESS CONTROL
  // ============================================================================

  /**
   * Check if a node type is available for current workflow type
   */
  isNodeAvailable(nodeType) {
    const config = NODE_TYPE_CONFIG[nodeType];
    if (!config) return false;
    return config.supportedTypes.includes(this.workflowType);
  }

  /**
   * Get all available node types for current workflow type
   */
  getAvailableNodeTypes() {
    return Object.values(NODE_TYPE_CONFIG)
      .filter(config => config.supportedTypes.includes(this.workflowType))
      .map(config => config.id);
  }

  /**
   * Check if a feature is available for a node type
   */
  isFeatureAvailable(nodeType, feature) {
    const config = NODE_TYPE_CONFIG[nodeType];
    if (!config || !config.features) return false;

    const typeKey = this.isStatic ? 'static' : 'ai';
    return config.features[typeKey]?.includes(feature) ?? false;
  }

  /**
   * Get all available features for a node type
   */
  getNodeFeatures(nodeType) {
    const config = NODE_TYPE_CONFIG[nodeType];
    if (!config || !config.features) return [];

    const typeKey = this.isStatic ? 'static' : 'ai';
    return config.features[typeKey] || [];
  }

  // ============================================================================
  // SIDEBAR MENU
  // ============================================================================

  /**
   * Filter sidebar menu items based on workflow type
   */
  filterSidebarMenuItems(menuItems) {
    return menuItems.filter(item => this.isNodeAvailable(item.id));
  }

  /**
   * Get default sidebar menu structure for current workflow type
   */
  getSidebarMenuItems(editable = true) {
    const items = [
      { id: 'conversation', name: 'Conversation Node', subItems: [] },
      { id: 'knowledge', name: 'Knowledge Node', subItems: [] },
      { id: 'call_connect', name: 'Call Connect Node', subItems: [] },
      { id: 'appointment', name: 'Appointment Node', subItems: [] },
      { id: 'chat_transfer', name: 'Chat Transfer Node', subItems: [] },
      { id: 'variable', name: 'Extract Variable Node', subItems: [] },
      { id: 'api', name: 'API Node', subItems: [] },
      { id: 'condition', name: 'Condition Node', subItems: [] },
      { id: 'end', name: 'End Node', subItems: [] }
    ];

    return items
      .filter(item => this.isNodeAvailable(item.id))
      .map(item => ({
        ...item,
        disabled: !editable
      }));
  }

  // ============================================================================
  // NODE VISIBILITY
  // ============================================================================

  /**
   * Check if transitions should be shown for conversation node
   */
  shouldShowConversationTransitions() {
    return this.isAI;
  }

  /**
   * Check if user option handles should be shown for conversation node
   */
  shouldShowUserOptionHandles() {
    return this.isStatic;
  }

  /**
   * Get conversation node layout configuration
   */
  getConversationNodeConfig() {
    return {
      showPromptType: this.isAI,
      showTransitions: this.isAI,
      showUserOptionHandles: this.isStatic,
      showUserOptions: true,
      showSkippable: this.isStatic,
      handleType: this.isStatic ? 'userOption' : 'transition'
    };
  }

  getEndNodeConfig() {
    return {
      showPromptType: false,
      showEndingStatement: this.isAI,   // AI WF only
      showClosingStatement: true
    };
  }

  // ============================================================================
  // JSON PAYLOAD FORMATTING
  // ============================================================================

  /**
   * Format nodes for API payload based on workflow type
   */
  formatNodesForPayload(nodes) {
    if (!Array.isArray(nodes)) return [];

    if (this.isStatic) {
      // For static workflows: remove source/target handles from conversation nodes without userOptions
      return nodes.map(node => {
        if (node.type === 'conversation') {
          const hasUserOptions =
            node.data?.userOptions && node.data.userOptions.length > 0;
          if (!hasUserOptions) {
            const { sourceHandle, targetHandle, ...restNode } = node;
            return restNode;
          }
        }
        return node;
      });
    }

    // AI workflows: return as-is
    return nodes;
  }

  /**
   * Format edges for API payload based on workflow type
   */
  formatEdgesForPayload(edges, nodes) {
    if (!Array.isArray(edges)) return [];

    if (this.isStatic) {
      // For static workflows: identify conversation nodes without userOptions
      const conversationNodesWithoutOptions = new Set();

      nodes?.forEach(node => {
        if (node.type === 'conversation') {
          const hasUserOptions =
            node.data?.userOptions && node.data.userOptions.length > 0;
          if (!hasUserOptions) {
            conversationNodesWithoutOptions.add(node.id);
          }
        }
      });

      // Remove source/target handles from edges where source is a conversation node without userOptions
      return edges.map(edge => {
        if (conversationNodesWithoutOptions.has(edge.source)) {
          const { sourceHandle, targetHandle, ...restEdge } = edge;
          return restEdge;
        }
        return edge;
      });
    }

    // AI workflows: return as-is
    return edges;
  }

  /**
   * Create complete payload for API
   */
  createPayload({ chatBotId, nodes, edges }) {
    const processedNodes = this.formatNodesForPayload(nodes);
    const processedEdges = this.formatEdgesForPayload(edges, nodes);

    return {
      chatBotId,
      nodesJson: JSON.stringify(processedNodes),
      edgesJson: JSON.stringify(processedEdges),
      workflowType: this.workflowType
    };
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  /**
   * Get validation rules for a node type
   */
  getValidationRules(nodeType) {
    const baseRules = {
      conversation: {
        incoming: 1,
        outgoing: 1,
        requiredFields: ['text']
      },
      end: {
        incoming: 1,
        outgoing: 0
      },
      knowledge: {
        incoming: 1,
        outgoing: 1
      },
      api: {
        incoming: 1,
        outgoing: 2
      },
      call_connect: {
        incoming: 1,
        outgoing: 3
      },
      appointment: {
        incoming: 1,
        outgoing: 4
      },
      chat_transfer: {
        incoming: 1,
        outgoing: 2
      },
      variable: {
        incoming: 1,
        outgoing: 2
      }
    };

    const rules = baseRules[nodeType] || {};

    // Adjust rules for workflow type
    if (nodeType === 'conversation' && this.isStatic) {
      return {
        ...rules,
        // Static workflows can have multiple outgoing edges (one per userOption)
        outgoing: 'variable'
      };
    }

    return rules;
  }

  /**
   * Validate a node based on workflow type rules
   */
  validateNode(node, edges) {
    const rules = this.getValidationRules(node.type);
    const errors = [];

    if (!rules) return { valid: true, errors };

    // Count incoming/outgoing edges
    const incoming = edges.filter(e => e.target === node.id).length;
    const outgoing = edges.filter(e => e.source === node.id).length;

    if (rules.incoming && incoming < rules.incoming) {
      errors.push(
        `${node.type} node must have at least ${rules.incoming} incoming connection(s)`
      );
    }

    if (rules.outgoing === 'variable') {
      // For static conversation nodes, check userOptions count
      if (node.type === 'conversation' && this.isStatic) {
        const userOptionsCount = node.data?.userOptions?.length || 0;
        if (outgoing < userOptionsCount && userOptionsCount > 0) {
          errors.push(
            `Conversation node should have connections for all ${userOptionsCount} quick replies`
          );
        }
      }
      // Condition / paths: each transition should have an outgoing edge
      if (node.type === 'condition') {
        const transitionCount = node.data?.transitions?.length || 0;
        if (transitionCount > 0 && outgoing < transitionCount) {
          errors.push(
            `Condition node should have connections for all ${transitionCount} condition path(s)`
          );
        }
      }
    } else if (rules.outgoing && outgoing < rules.outgoing) {
      errors.push(
        `${node.type} node must have at least ${rules.outgoing} outgoing connection(s)`
      );
    }

    // Check required fields
    if (rules.requiredFields) {
      rules.requiredFields.forEach(field => {
        const value = node.data?.[field];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors.push(`${node.type} node must have ${field}`);
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get display name for workflow type
   */
  getDisplayName() {
    return this.isStatic ? 'Static Workflow' : 'AI Workflow';
  }

  /**
   * Get CSS class for workflow type chip
   */
  getChipClass() {
    return this.isStatic ? 'workflow-type-static' : 'workflow-type-ai';
  }

  /**
   * Check if workflow type is valid
   */
  isValid() {
    return (
      this.workflowType === WORKFLOW_TYPES.AI ||
      this.workflowType === WORKFLOW_TYPES.STATIC
    );
  }
}

/**
 * Factory function to create adapter instance
 */
export const createWorkflowTypeAdapter = workflowType => {
  return new WorkflowTypeAdapter(workflowType);
};

/**
 * Hook-compatible helper for functional components
 */
export const useWorkflowTypeAdapter = workflowType => {
  return createWorkflowTypeAdapter(workflowType);
};

export default WorkflowTypeAdapter;
